import React, { useState, useEffect } from 'react';
import { Menu, X, ShoppingCart, Heart, User, Camera } from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from '../context/ThemeContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { themeData } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', href: '#home' },
    { name: 'Specials', href: '#specials' },
    { name: 'Valentine Gifts', href: '#valentine' },
    { name: 'Categories', href: '#categories' },
    { name: 'Customize', href: '#customize' },
    { name: 'Locations', href: '#locations' },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-[#1a1a2e]/95 backdrop-blur-md shadow-lg' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#home" className="flex items-center gap-3">
            <img 
              src="/genx-logo.png" 
              alt="Gen-X Photo Studio" 
              className="h-12 w-auto"
              style={{ filter: 'drop-shadow(0 0 10px rgba(255,255,255,0.3))' }}
            />
            <div className="hidden sm:block">
              <span className="text-white font-bold text-xl">Gen-X</span>
              <span className="text-white/60 text-xs block">Photo Studio JA</span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="text-white/80 hover:text-white transition-colors text-sm font-medium relative group"
              >
                {item.name}
                <span 
                  className="absolute -bottom-1 left-0 w-0 h-0.5 transition-all group-hover:w-full"
                  style={{ backgroundColor: themeData.colors.primary }}
                />
              </a>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-4">
            <button className="p-2 text-white/80 hover:text-white transition-colors relative">
              <Heart className="w-5 h-5" />
              <span 
                className="absolute -top-1 -right-1 w-4 h-4 text-xs rounded-full flex items-center justify-center text-white"
                style={{ backgroundColor: themeData.colors.primary }}
              >
                0
              </span>
            </button>
            <button className="p-2 text-white/80 hover:text-white transition-colors relative">
              <ShoppingCart className="w-5 h-5" />
              <span 
                className="absolute -top-1 -right-1 w-4 h-4 text-xs rounded-full flex items-center justify-center text-white"
                style={{ backgroundColor: themeData.colors.secondary }}
              >
                0
              </span>
            </button>
            <Button 
              className="text-white font-medium px-6"
              style={{ backgroundColor: themeData.colors.primary }}
            >
              <User className="w-4 h-4 mr-2" />
              Sign In
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-[#1a1a2e]/98 backdrop-blur-md border-t border-white/10">
          <div className="px-4 py-6 space-y-4">
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="block text-white/80 hover:text-white transition-colors py-2 text-lg"
                onClick={() => setIsMenuOpen(false)}
              >
                {item.name}
              </a>
            ))}
            <div className="pt-4 flex gap-4">
              <Button variant="outline" className="flex-1 border-white/20 text-white">
                <Heart className="w-4 h-4 mr-2" />
                Wishlist
              </Button>
              <Button 
                className="flex-1 text-white"
                style={{ backgroundColor: themeData.colors.primary }}
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Cart
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
